import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container, Paper, Typography, TextField, Button,
  Alert, CircularProgress, Box, Link, List, ListItem,
  ListItemIcon, ListItemText,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import axios from 'axios';

const rules = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'Uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'Lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'Number', test: (p) => /\d/.test(p) },
  { label: 'Special character (@$!%*?&)', test: (p) => /[@$!%*?&]/.test(p) },
];

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const allRulesPass = rules.every((r) => r.test(password));
  const passwordsMatch = password === confirm && confirm !== '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allRulesPass || !passwordsMatch) return;
    setLoading(true);
    setError('');
    try {
      await axios.post('/api/auth/reset-password', { token, password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.error || 'This reset link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error">
          Invalid reset link. <Link component={RouterLink} to="/forgot-password">Request a new one.</Link>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Choose a new password
        </Typography>

        {success ? (
          <Alert severity="success">Password updated! Redirecting to login…</Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TextField
              label="New password"
              type="password"
              fullWidth
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2 }}
            />

            {/* Live requirements checklist */}
            <List dense sx={{ mb: 2 }}>
              {rules.map((r) => {
                const ok = r.test(password);
                return (
                  <ListItem key={r.label} disableGutters sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {ok
                        ? <CheckCircleIcon color="success" fontSize="small" />
                        : <RadioButtonUncheckedIcon color="disabled" fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={r.label}
                      primaryTypographyProps={{ variant: 'caption', color: ok ? 'success.main' : 'text.secondary' }}
                    />
                  </ListItem>
                );
              })}
            </List>

            <TextField
              label="Confirm password"
              type="password"
              fullWidth
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              error={confirm !== '' && !passwordsMatch}
              helperText={confirm !== '' && !passwordsMatch ? 'Passwords do not match' : ''}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading || !allRulesPass || !passwordsMatch}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Set new password'}
            </Button>
          </Box>
        )}

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Link component={RouterLink} to="/login" variant="body2">
            Back to login
          </Link>
        </Box>
      </Paper>
    </Container>
  );
};

export default ResetPassword;
